import React, { useMemo, useState } from "react";
import {
  Users,
  UserCheck,
  UserX,
  UserPlus,
  Mail,
  Phone,
  Building2,
  User,
  IndianRupee,
  Wallet,
  AlertTriangle,
  TrendingUp,
} from "lucide-react";
import { isInFilter } from "../utils/dateFilter";
import { aggregateReceivables, formatCurrencyCompact, formatCurrency } from "./customerFinance";

const FILTER_TABS = ["Today", "Yesterday", "Weekly", "Monthly", "All Time", "Custom Date"];

export function isCustomerActive(status) {
  const normalized = String(status ?? "active").trim().toLowerCase();
  return normalized === "" || normalized === "active";
}

function getFilterLabel(filter) {
  if (typeof filter === "string") return filter;
  if (filter?.type === "Custom Date" && filter.from && filter.to) {
    return `${filter.from} to ${filter.to}`;
  }
  return "Selected period";
}

const SummaryDateFilter = ({ value, onChange }) => {
  const [activeTab, setActiveTab] = useState(
    typeof value === "string" ? value : value?.type === "Custom Date" ? "Custom Date" : "All Time"
  );
  const [showCustom, setShowCustom] = useState(activeTab === "Custom Date");
  const [tempRange, setTempRange] = useState({
    from: value?.from || "",
    to: value?.to || "",
  });

  const handleTabClick = (tab) => {
    if (tab === "Custom Date") {
      setActiveTab("Custom Date");
      setShowCustom(true);
      return;
    }
    setActiveTab(tab);
    setShowCustom(false);
    onChange(tab);
  };

  const handleApplyCustom = () => {
    if (!tempRange.from || !tempRange.to || tempRange.from > tempRange.to) return;
    onChange({ type: "Custom Date", from: tempRange.from, to: tempRange.to });
    setShowCustom(false);
  };

  return (
    <div className="w-full sm:w-auto sm:min-w-[280px]">
      <div className="flex items-center gap-1 dash-filter-scroll rounded-xl border border-line bg-surface-2 p-1">
        {FILTER_TABS.map((tab) => {
          const isActive = activeTab === tab;
          return (
            <button
              key={tab}
              type="button"
              onClick={() => handleTabClick(tab)}
              className={`shrink-0 rounded-lg px-2.5 py-1.5 text-[11px] sm:text-xs font-semibold transition-colors whitespace-nowrap ${
                isActive
                  ? "bg-primary text-white shadow-sm"
                  : "text-ink-muted hover:bg-white/10 hover:text-ink"
              }`}
            >
              {tab}
            </button>
          );
        })}
      </div>

      {showCustom && (
        <div className="mt-2 rounded-xl border border-dashed border-line-strong bg-surface-2 p-2.5">
          <div className="flex flex-col sm:flex-row gap-2">
            <label className="flex flex-col gap-1 text-[11px] font-semibold text-ink-secondary flex-1">
              From
              <input
                type="date"
                value={tempRange.from}
                onChange={(e) => setTempRange((prev) => ({ ...prev, from: e.target.value }))}
                className="rounded-lg border border-line bg-surface text-ink px-2 py-1.5 text-xs outline-none focus:border-primary [color-scheme:dark]"
              />
            </label>
            <label className="flex flex-col gap-1 text-[11px] font-semibold text-ink-secondary flex-1">
              To
              <input
                type="date"
                value={tempRange.to}
                onChange={(e) => setTempRange((prev) => ({ ...prev, to: e.target.value }))}
                className="rounded-lg border border-line bg-surface text-ink px-2 py-1.5 text-xs outline-none focus:border-primary [color-scheme:dark]"
              />
            </label>
          </div>
          <div className="mt-2 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setShowCustom(false);
                if (typeof value === "string") setActiveTab(value);
              }}
              className="rounded-lg border border-line px-3 py-1 text-xs font-semibold text-ink-secondary hover:bg-white/10"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleApplyCustom}
              disabled={!tempRange.from || !tempRange.to || tempRange.from > tempRange.to}
              className="rounded-lg bg-primary px-3 py-1 text-xs font-semibold text-white hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const MetricCard = ({ label, value, hint, tone = "default", icon: Icon, active, onClick }) => {
  const toneClass =
    tone === "success"
      ? "text-lime-300"
      : tone === "warn"
        ? "text-amber-300"
        : tone === "danger"
          ? "text-rose-300"
          : "text-ink";

  const Tag = onClick ? "button" : "div";

  return (
    <Tag
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className={`rounded-lg border px-3 py-2 min-w-[118px] text-left transition-all ${
        active
          ? "border-brand-cyan bg-white/10 ring-2 ring-cyan-500/30 shadow-sm"
          : "border-line bg-white/5 hover:border-brand-cyan/40 hover:bg-white/10"
      } ${onClick ? "cursor-pointer" : ""}`}
      aria-pressed={onClick ? active : undefined}
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <p className="text-[11px] uppercase tracking-wide text-ink-muted font-semibold">{label}</p>
        {Icon ? <Icon size={14} className="text-ink-muted shrink-0" /> : null}
      </div>
      <p className={`text-lg font-bold ${toneClass}`}>{value}</p>
      {hint ? <p className="text-[11px] text-ink-muted mt-0.5">{hint}</p> : null}
    </Tag>
  );
};

export default function CustomerPageSummary({
  customers = [],
  financials = {},
  activeFilter = "all",
  onFilterChange,
}) {
  const [filter, setFilter] = useState("All Time");

  // Receivables are a lifetime business KPI — computed across ALL customers,
  // independent of the period/status filter above.
  const receivables = useMemo(
    () => aggregateReceivables(customers, financials),
    [customers, financials]
  );
  const collectionRate =
    receivables.invoicedTotal > 0
      ? Math.round((receivables.collected / receivables.invoicedTotal) * 100)
      : 0;

  const summary = useMemo(() => {
    const periodCustomers = (Array.isArray(customers) ? customers : []).filter((customer) =>
      isInFilter(customer.createdAt, filter)
    );

    const activeList = periodCustomers.filter((c) => isCustomerActive(c.status));
    const inactiveList = periodCustomers.filter((c) => !isCustomerActive(c.status));
    const withEmail = periodCustomers.filter((c) => String(c.email || "").trim()).length;
    const withPhone = periodCustomers.filter((c) => String(c.phone1 || "").trim()).length;
    const individual = periodCustomers.filter((c) => c.customerType === "Individual").length;
    const company = periodCustomers.filter((c) => c.customerType === "Company").length;

    const activeCount = activeList.length;
    const inactiveCount = inactiveList.length;
    const total = periodCustomers.length;
    const statusTotal = total || 1;
    const addedToday = (Array.isArray(customers) ? customers : []).filter((c) =>
      isInFilter(c.createdAt, "Today")
    ).length;

    return {
      filterLabel: getFilterLabel(filter),
      total,
      activeCount,
      inactiveCount,
      addedToday,
      withEmail,
      withPhone,
      withoutContact: periodCustomers.filter(
        (c) => !String(c.email || "").trim() && !String(c.phone1 || "").trim()
      ).length,
      individual,
      company,
      activePct: Math.round((activeCount / statusTotal) * 100),
      inactivePct: Math.round((inactiveCount / statusTotal) * 100),
    };
  }, [customers, filter]);

  const switchFilter = (next) => {
    if (onFilterChange) onFilterChange(next);
  };

  return (
    <section className="customer-page-summary mb-4">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div>
          <h2 className="text-base sm:text-lg font-bold text-ink">Customer Summary</h2>
          <p className="text-xs sm:text-sm text-ink-muted">
            Active vs inactive customers and contact coverage — {summary.filterLabel}
          </p>
        </div>
        <SummaryDateFilter value={filter} onChange={setFilter} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3 mb-3">
        <div className="rounded-xl border border-brand-cyan/20 bg-gradient-to-br from-brand-cyan/10 to-primary/10 p-3 sm:p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center text-cyan-300 shadow-sm">
              <Users size={16} />
            </div>
            <h3 className="text-sm sm:text-base font-bold text-ink">Status</h3>
          </div>
          <div className="flex gap-2 dash-metric-scroll dash-metric-scroll--customers pb-1">
            <MetricCard
              label="Total Customers"
              value={summary.total}
              hint="In selected period"
              icon={Users}
              active={activeFilter === "all"}
              onClick={() => switchFilter("all")}
            />
            <MetricCard
              label="Active"
              value={summary.activeCount}
              hint={`${summary.activePct}% of total`}
              icon={UserCheck}
              tone="success"
              active={activeFilter === "active"}
              onClick={() => switchFilter("active")}
            />
            <MetricCard
              label="Inactive"
              value={summary.inactiveCount}
              hint={`${summary.inactivePct}% of total`}
              icon={UserX}
              tone={summary.inactiveCount > 0 ? "danger" : "default"}
              active={activeFilter === "inactive"}
              onClick={() => switchFilter("inactive")}
            />
            <MetricCard
              label="Added Today"
              value={summary.addedToday}
              hint="New sign-ups today"
              icon={UserPlus}
              tone={summary.addedToday > 0 ? "warn" : "default"}
            />
          </div>
        </div>

        <div className="rounded-xl border border-primary/20 bg-gradient-to-br from-primary/10 to-brand-fuchsia/10 p-3 sm:p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center text-violet-300 shadow-sm">
              <Mail size={16} />
            </div>
            <h3 className="text-sm sm:text-base font-bold text-ink">Profile</h3>
          </div>
          <div className="flex gap-2 dash-metric-scroll dash-metric-scroll--customers pb-1">
            <MetricCard
              label="With Email"
              value={summary.withEmail}
              hint="Has email on file"
              icon={Mail}
            />
            <MetricCard
              label="With Phone"
              value={summary.withPhone}
              hint="Has primary phone"
              icon={Phone}
            />
            <MetricCard
              label="Individual"
              value={summary.individual}
              icon={User}
            />
            <MetricCard
              label="Company"
              value={summary.company}
              icon={Building2}
            />
            <MetricCard
              label="No Contact"
              value={summary.withoutContact}
              hint="Missing email & phone"
              tone={summary.withoutContact > 0 ? "warn" : "default"}
            />
          </div>
        </div>
      </div>

      {receivables.customersWithInvoices > 0 && (
        <div className="rounded-xl border border-brand-lime/20 bg-gradient-to-br from-brand-lime/10 to-emerald-500/10 p-3 sm:p-4 mb-3">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center text-lime-300 shadow-sm">
              <IndianRupee size={16} />
            </div>
            <div>
              <h3 className="text-sm sm:text-base font-bold text-ink">Receivables</h3>
              <p className="text-[11px] text-ink-muted">Lifetime value &amp; dues across all customers</p>
            </div>
          </div>
          <div className="flex gap-2 dash-metric-scroll dash-metric-scroll--customers pb-1">
            <MetricCard
              label="Total Invoiced"
              value={formatCurrencyCompact(receivables.invoicedTotal)}
              hint={`${receivables.customersWithInvoices} billed customer(s)`}
              icon={TrendingUp}
            />
            <MetricCard
              label="Collected"
              value={formatCurrencyCompact(receivables.collected)}
              hint={`${collectionRate}% collection rate`}
              icon={Wallet}
              tone="success"
            />
            <MetricCard
              label="Outstanding"
              value={formatCurrencyCompact(receivables.outstanding)}
              hint={`${receivables.customersWithDues} customer(s) owe you`}
              icon={IndianRupee}
              tone={receivables.outstanding > 0 ? "warn" : "default"}
            />
            <MetricCard
              label="Overdue"
              value={formatCurrencyCompact(receivables.overdueAmount)}
              hint="Past due date"
              icon={AlertTriangle}
              tone={receivables.overdueAmount > 0 ? "danger" : "default"}
            />
          </div>
          {receivables.outstanding > 0 && (
            <p className="mt-2 text-[11px] text-slate-600">
              <strong>{formatCurrency(receivables.outstanding)}</strong> is yet to be collected
              {receivables.overdueAmount > 0
                ? `, of which ${formatCurrency(receivables.overdueAmount)} is overdue.`
                : "."}
            </p>
          )}
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-3 sm:p-4 mb-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
          Active vs inactive
        </p>
        <div className="flex h-3 w-full overflow-hidden rounded-full bg-slate-100">
          {summary.activeCount > 0 && (
            <button
              type="button"
              title={`Active: ${summary.activeCount}`}
              onClick={() => switchFilter("active")}
              className="h-full transition-opacity hover:opacity-80"
              style={{
                width: `${(summary.activeCount / (summary.total || 1)) * 100}%`,
                backgroundColor: "#22c55e",
                minWidth: "4px",
              }}
              aria-label={`Active ${summary.activeCount}`}
            />
          )}
          {summary.inactiveCount > 0 && (
            <button
              type="button"
              title={`Inactive: ${summary.inactiveCount}`}
              onClick={() => switchFilter("inactive")}
              className="h-full transition-opacity hover:opacity-80"
              style={{
                width: `${(summary.inactiveCount / (summary.total || 1)) * 100}%`,
                backgroundColor: "#ef4444",
                minWidth: "4px",
              }}
              aria-label={`Inactive ${summary.inactiveCount}`}
            />
          )}
        </div>
        <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-slate-600">
          <button
            type="button"
            onClick={() => switchFilter("active")}
            className={`inline-flex items-center gap-1 hover:text-slate-900 ${
              activeFilter === "active" ? "font-bold text-slate-900" : ""
            }`}
          >
            <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" />
            Active ({summary.activeCount})
          </button>
          <button
            type="button"
            onClick={() => switchFilter("inactive")}
            className={`inline-flex items-center gap-1 hover:text-slate-900 ${
              activeFilter === "inactive" ? "font-bold text-slate-900" : ""
            }`}
          >
            <span className="inline-block w-2 h-2 rounded-full bg-red-500" />
            Inactive ({summary.inactiveCount})
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs text-slate-600">
        <div className="flex items-start gap-2 rounded-lg bg-white border border-blue-100 px-3 py-2">
          <UserCheck size={14} className="text-emerald-600 mt-0.5 shrink-0" />
          <span>
            {summary.activeCount > 0
              ? `${summary.activeCount} active customer(s) — ${summary.activePct}% of your base in this period.`
              : "No active customers in this period."}
          </span>
        </div>
        <div className="flex items-start gap-2 rounded-lg bg-white border border-red-100 px-3 py-2">
          <UserX size={14} className="text-red-500 mt-0.5 shrink-0" />
          <span>
            {summary.inactiveCount > 0
              ? `${summary.inactiveCount} inactive customer(s) may need re-engagement or cleanup.`
              : "No inactive customers in this period."}
          </span>
        </div>
        <div className="flex items-start gap-2 rounded-lg bg-white border border-slate-200 px-3 py-2">
          <Phone size={14} className="text-blue-600 mt-0.5 shrink-0" />
          <span>
            {summary.withoutContact > 0
              ? `${summary.withoutContact} customer(s) are missing both email and phone.`
              : "All customers in this period have email or phone on file."}
          </span>
        </div>
      </div>
    </section>
  );
}
